import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pause, Play } from "@phosphor-icons/react";

/**
 * BulkAvailabilityDialog — the 86 button for a whole rush.
 *
 * Managers pick a filter (all items / one category / one modifier group)
 * and either Pause (available=false) or Resume (available=true). A dry-run
 * preview shows which items will flip BEFORE the change is committed, so
 * pressing the button during a rush can't nuke the wrong menu.
 */
const CATEGORIES = [
  { value: "appetizers", label: "Appetizers" },
  { value: "mains", label: "Mains" },
  { value: "drinks", label: "Drinks" },
  { value: "desserts", label: "Desserts" },
];

const BulkAvailabilityButton = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [filterType, setFilterType] = useState("category");
  const [filterValue, setFilterValue] = useState("");
  const [action, setAction] = useState("pause");    // "pause" | "resume"
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [groups, setGroups] = useState([]);

  // Preload modifier group names so the picker offers real options.
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/modifier-templates`).then((res) => {
      setGroups(res.data || []);
    }).catch(() => setGroups([]));
  }, [open]);

  // Reset preview whenever the filter or action changes.
  useEffect(() => {
    setPreview(null);
  }, [filterType, filterValue, action]);

  const canPreview = filterType === "all"
    ? true
    : filterValue.length > 0;

  const runPreview = async () => {
    if (!canPreview) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/menu/bulk-availability`, {
        filter_type: filterType,
        filter_value: filterType === "all" ? null : filterValue,
        available: action === "resume",
        dry_run: true,
      });
      setPreview(res.data);
    } catch (e) {
      toast.error("Preview failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/menu/bulk-availability`, {
        filter_type: filterType,
        filter_value: filterType === "all" ? null : filterValue,
        available: action === "resume",
      });
      const verb = action === "pause" ? "paused" : "resumed";
      toast.success(`${res.data.updated_count} item(s) ${verb}`);
      setOpen(false);
      setPreview(null);
      setFilterValue("");
      if (typeof onDone === "function") onDone();
    } catch (e) {
      toast.error("Bulk update failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const Icon = action === "pause" ? Pause : Play;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => setOpen(true)}
        data-testid="bulk-availability-btn"
        title="Pause or resume many items at once"
      >
        <Pause size={16} weight="bold" /> Pause / Resume
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="bulk-availability-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon size={22} weight="fill" className={action === "pause" ? "text-amber-500" : "text-emerald-600"} />
              {action === "pause" ? "Pause items (86)" : "Resume items"}
            </DialogTitle>
            <DialogDescription>
              Toggle the availability of many items in one shot. Use during a
              rush to 86 a whole category, or to bring an ingredient-restricted
              group back online.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Action toggle */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={action === "pause" ? "default" : "outline"}
                onClick={() => setAction("pause")}
                className="gap-2"
                data-testid="bulk-action-pause"
              >
                <Pause size={18} weight="bold" /> Pause (86)
              </Button>
              <Button
                variant={action === "resume" ? "default" : "outline"}
                onClick={() => setAction("resume")}
                className="gap-2"
                data-testid="bulk-action-resume"
              >
                <Play size={18} weight="bold" /> Resume
              </Button>
            </div>

            {/* Filter type */}
            <div className="space-y-2">
              <Label>Apply to</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="bulk-filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">A whole category</SelectItem>
                  <SelectItem value="modifier_group">Items with a modifier group</SelectItem>
                  <SelectItem value="all">Every menu item</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter value */}
            {filterType === "category" && (
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={filterValue} onValueChange={setFilterValue}>
                  <SelectTrigger data-testid="bulk-filter-value-category">
                    <SelectValue placeholder="Pick a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {filterType === "modifier_group" && (
              <div className="space-y-2">
                <Label>Modifier Group</Label>
                <Select value={filterValue} onValueChange={setFilterValue}>
                  <SelectTrigger data-testid="bulk-filter-value-group">
                    <SelectValue placeholder="Pick a modifier group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id || g.group_name} value={g.group_name}>
                        {g.group_name}
                      </SelectItem>
                    ))}
                    {groups.length === 0 && (
                      <div className="px-2 py-2 text-xs text-muted-foreground">No groups defined</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Preview */}
            {preview && (
              <div className="rounded-lg border border-border p-3 space-y-2" data-testid="bulk-preview">
                <div className="text-sm">
                  <b>{preview.matched_count || 0}</b> item(s) match ·{" "}
                  <b>{preview.affected_count || 0}</b> will flip to{" "}
                  <b>{action === "pause" ? "paused" : "available"}</b>
                </div>
                {preview.affected_items?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto text-xs space-y-0.5">
                    {preview.affected_items.map((m, i) => (
                      <div key={i} className="border-b border-border/50 pb-0.5">
                        {m.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); setPreview(null); }}
              disabled={busy}
              data-testid="bulk-cancel-btn"
            >
              Cancel
            </Button>
            {!preview ? (
              <Button
                onClick={runPreview}
                disabled={busy || !canPreview}
                data-testid="bulk-preview-btn"
              >
                {busy ? "Checking..." : "Preview"}
              </Button>
            ) : (
              <Button
                onClick={commit}
                disabled={busy || (preview.affected_count || 0) === 0}
                data-testid="bulk-commit-btn"
                className={action === "pause" ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-700"}
              >
                {busy ? "Applying..." : `Confirm ${action === "pause" ? "Pause" : "Resume"}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkAvailabilityButton;
